import {
  Card,
  CardContent,
  Chip,
  Modal,
  Typography,
  Button,
  Box,
  TextField,
  InputLabel,
  IconButton,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import React, { useState } from "react";
import { callBackendApi } from "../../methods/axiosInstances/backendInstance/apiRequest";

const evidenceFieldMap = {
  fraudulent: [
    "customer_name",
    "customer_email_address",
    "product_description",
    "customer_purchase_ip",
  ],
  duplicate: [
    "customer_name",
    "customer_email_address",
    "duplicate_charge_explanation",
    "customer_purchase_ip",
  ],
  canceled: [
    "customer_name",
    "customer_email_address",
    "product_description",
    "refund_refusal_explanation",
    "customer_communication",
  ],
  not_received: [
    "customer_name",
    "customer_email_address",
    "shipping_address",
    "shipping_date",
    "shipping_carrier",
    "shipping_tracking_number",
  ],
  merchandise_not_as_described: [
    "customer_name",
    "customer_email_address",
    "product_description",
    "customer_communication",
  ],
  service_not_as_described: [
    "customer_name",
    "customer_email_address",
    "product_description",
    "service_date",
    "customer_communication",
  ],
  no_valid_authorization: [
    "customer_name",
    "customer_email_address",
    "product_description",
  ],
  other: [
    "customer_name",
    "customer_email_address",
    "product_description",
    "customer_communication",
  ],
  product_not_received: [
    "customer_name",
    "customer_email_address",
    "product_description",
    "shipping_address",
    "shipping_date",
    "shipping_carrier",
    "shipping_tracking_number",
  ],
};

const fileFieldMap = {
  fraudulent: ["receipt"],
  duplicate: ["receipt", "duplicate_charge_documentation"],
  canceled: ["receipt"],
  not_received: ["receipt", "shipping_documentation"],
  merchandise_not_as_described: ["receipt"],
  service_not_as_described: ["service_documentation"],
  no_valid_authorization: ["receipt"],
  other: ["uncategorized_file"],
  product_not_received: ["receipt", "shipping_documentation"],
};

const DisputeCard = ({ dispute, getDisputes }) => {
  const [selectedDispute, setSelectedDispute] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  const [evidence, setEvidence] = useState({});
  const [fileUploads, setFileUploads] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState([]);

  const handleChange = (e) => {
    setEvidence({ ...evidence, [e.target.name]: e.target.value });
  };

  const handleFileChange = (e) => {
    setFileUploads({ ...fileUploads, [e.target.name]: e.target.files[0] });
  };

  const openEvidenceModal = (dispute) => {
    setSelectedDispute(dispute);
    const defaultEvidence = {};
    const reasonFields = evidenceFieldMap[dispute.reason] || [];

    reasonFields.forEach((field) => {
      defaultEvidence[field] =
        dispute.evidence?.[field] ||
        dispute[field] ||
        (field === "product_description" ? dispute.reason : "") ||
        "";
    });

    setEvidence(defaultEvidence);
    setFileUploads({});
    setModalOpen(true);
  };

  const closeEvidenceModal = () => {
    setModalOpen(false);
    setSelectedDispute(null);
    setFileUploads({});
  };

  const handleSubmit = async () => {
    setErrors([]);
    setSubmitting(true);

    const formData = new FormData();
    formData.append("disputeId", selectedDispute.id);

    Object.entries(evidence).forEach(([key, value]) => {
      formData.append(`evidence[${key}]`, value);
    });

    Object.entries(fileUploads).forEach(([key, file]) => {
      if (file) {
        formData.append(key, file);
      }
    });

    const response = await callBackendApi(
      "stripe/disputes/respond",
      "POST",
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      }
    );

    if (response.success) {
      alert("Evidence submitted successfully!");
      closeEvidenceModal();
      getDisputes();
    } else {
      console.log(response);
      setErrors(response.error);
      alert("Error submitting evidence.");
    }

    setSubmitting(false);
  };

  const visibleFileFields = selectedDispute
    ? fileFieldMap[selectedDispute.reason] || []
    : [];

  return (
    <Card>
      <CardContent>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Button
          variant="outlined"
          size="small"
          onClick={() => openEvidenceModal(dispute)}
          sx={{ mt: 2 }}
        >
          View Evidence
        </Button>
          <Typography variant="h6">General Info</Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Typography>
              <strong>Dispute ID:</strong> {dispute.id}
            </Typography>
            <Typography>
              <strong>Status:</strong> {RenderStatusChip(dispute.status)}
            </Typography>
            <Typography>
              <strong>Reason:</strong> {dispute.reason}
            </Typography>
            <Typography>
              <strong>Amount:</strong> €{(dispute.amount / 100).toFixed(2)}
            </Typography>
            <Typography>
              <strong>Currency:</strong> {dispute.currency.toUpperCase()}
            </Typography>
            <Typography>
              <strong>Refundable:</strong>{" "}
              {dispute.is_charge_refundable ? "Yes" : "No"}
            </Typography>
            <Typography>
              <strong>Created At:</strong>{" "}
              {new Date(dispute.created * 1000).toLocaleString()}
            </Typography>
            <Typography>
              <strong>Respond By:</strong>{" "}
              {new Date(
                dispute.evidence_details?.due_by * 1000
              ).toLocaleDateString()}
            </Typography>
          </Box>

          <Typography variant="h6" sx={{ mt: 2 }}>
            Customer Info
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Typography>
              <strong>Name:</strong> {dispute.evidence?.customer_name || "N/A"}
            </Typography>
            <Typography>
              <strong>Email:</strong>{" "}
              {dispute.evidence?.customer_email_address || "N/A"}
            </Typography>
            <Typography>
              <strong>Purchase IP:</strong>{" "}
              {dispute.evidence?.customer_purchase_ip || "N/A"}
            </Typography>
          </Box>

          <Typography variant="h6" sx={{ mt: 2 }}>
            Payment Info
          </Typography>
          <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
            <Typography>
              <strong>Charge:</strong> {dispute.charge}
            </Typography>
            <Typography>
              <strong>Payment Method:</strong>{" "}
              {dispute.payment_method_details?.type}
            </Typography>
            <Typography>
              <strong>Card Brand:</strong>{" "}
              {dispute.payment_method_details?.card?.brand}
            </Typography>
            <Typography>
              <strong>Network Reason Code:</strong>{" "}
              {dispute.payment_method_details?.card?.network_reason_code}
            </Typography>
          </Box>
        </Box>
      </CardContent>

      <Modal open={modalOpen} onClose={() => {}} disableEscapeKeyDown>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 600,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
            maxHeight: "90vh",
            overflow: "auto",
          }}
        >
          {selectedDispute && (
            <>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={2}
              >
                <Typography variant="h6">
                  Submit Evidence for {selectedDispute.id}
                </Typography>
                <IconButton onClick={closeEvidenceModal}>
                  <CloseIcon />
                </IconButton>
              </Box>

              {(evidenceFieldMap[selectedDispute.reason] || []).map((field) => (
                <Box key={field} sx={{ mb: 2 }}>
                  <InputLabel htmlFor={field} sx={{ mb: 0.5 }}>
                    {field
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                  </InputLabel>
                  <TextField
                    id={field}
                    name={field}
                    value={evidence[field] || ""}
                    onChange={handleChange}
                    fullWidth
                    margin="normal"
                    multiline
                    rows={2}
                    error={errors[`evidence.${field}`] !== undefined}
                    helperText={errors[`evidence.${field}`]?.[0] || ""}
                  />
                </Box>
              ))}

              {visibleFileFields.map((field) => (
                <Box key={field} sx={{ mt: 2, mb: 2 }}>
                  <InputLabel htmlFor={field}>
                    {field
                      .replace(/_/g, " ")
                      .replace(/\b\w/g, (l) => l.toUpperCase())}
                  </InputLabel>
                  <input
                    type="file"
                    id={field}
                    name={field}
                    onChange={handleFileChange}
                    style={{
                      border: errors[field]
                        ? "1px solid red"
                        : "1px solid transparent",
                      padding: "0.5rem",
                      marginTop: "0.5rem",
                      marginBottom: "0.25rem",
                      borderRadius: "4px",
                      width: "100%",
                    }}
                  />
                  {errors[field] && (
                    <Typography color="error" variant="body2">
                      {errors[field][0]}
                    </Typography>
                  )}
                </Box>
              ))}

              <Button
                variant="contained"
                color="primary"
                onClick={handleSubmit}
                disabled={submitting}
                sx={{ mt: 2 }}
              >
                {submitting ? "Submitting..." : "Submit Response"}
              </Button>
            </>
          )}
        </Box>
      </Modal>
    </Card>
  );
};

const RenderStatusChip = (status) => {
  const statusMap = {
    needs_response: { label: "Needs Response", color: "warning" },
    under_review: { label: "Under Review", color: "info" },
    won: { label: "Won", color: "success" },
    lost: { label: "Lost", color: "error" },
    warning_closed: { label: "Warning Closed", color: "default" },
  };

  const { label, color } = statusMap[status] || {
    label: status,
    color: "default",
  };

  return <Chip label={label} color={color} size="small" />;
};

export default DisputeCard;
